import { useContext, useEffect, useState } from 'react';
import { defaultContext, useQuery } from '@tanstack/react-query';
import type { SocialPostPreviewCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { cardDecisionPrompt } from '../../../../lib/auto-approve-line';
import { useTranslation } from '../../../../lib/i18n';
import { useMarketingPublishTimeEnabled } from '../../../../lib/marketing-publish-time-flag';
import { socialPostTaskPollMs, socialPostTaskView } from '../../../../lib/social-post-task-state';
import { socialPostTapMessage, type DraftMessage } from '../../../../lib/draft-preview';
import type { ScheduledPostAction } from '../../../../lib/task-content-view';
import { useTasks } from '../../../../lib/tasks';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { taskDetailQuery, type TaskDetail } from '../../../../lib/use-task-detail';
import { useWorkspaces } from '../../../../lib/workspaces';
import { SocialOutcome } from '../../tasks/TaskContentSection';
import { ChoiceActionChip } from './ChoiceChips';

const MAX_TIMER_MS = 2_147_483_647;

interface SocialPostTaskStateProps {
  card: SocialPostPreviewCardMetadata;
  answered: boolean;
  canSend: boolean;
  onTap?: (tap: DraftMessage) => boolean;
}

export function SocialPostTaskState(props: SocialPostTaskStateProps) {
  const client = useContext(defaultContext);
  const enabled = useMarketingPublishTimeEnabled();
  return client && enabled === true && props.card.taskId ? <SocialPostTaskRead {...props} taskId={props.card.taskId} /> : null;
}

export function SocialPostEditFirstChip(props: SocialPostTaskStateProps) {
  const client = useContext(defaultContext);
  const enabled = useMarketingPublishTimeEnabled();
  return client && enabled === true && props.card.taskId && !props.answered && props.onTap ? <SocialPostEditFirstFromTasks {...props} taskId={props.card.taskId} /> : null;
}

function SocialPostEditFirstFromTasks({ card, canSend, onTap, taskId }: SocialPostTaskStateProps & { taskId: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { state } = useTasks();
  const workspaces = useWorkspaces();
  const tasks = state.kind === 'ready' ? state.tasks : undefined;
  const task = tasks?.find((row) => row.id === taskId);
  if (!task || !cardDecisionPrompt(tasks, card)) return null;
  const slug = workspaces.status === 'ready' ? (workspaces.workspaces.find((w) => w.id === task.workspaceId)?.slug ?? '') : '';
  if (!slug) return null;
  return (
    <ChoiceActionChip
      label={t('draftPreview.editFirst')}
      disabled={!canSend}
      testId="socialPostEditFirst"
      onPress={() => {
        logPageClickBi({ page_name: 'chat', button_name: 'social_post_edit_first' }, { additionalFields: { task_id: taskId } });
        onTap?.(socialPostTapMessage('edit', slug, taskId));
      }}
    />
  );
}

function SocialPostTaskRead({ card, answered, canSend, onTap, taskId }: SocialPostTaskStateProps & { taskId: string }) {
  const { i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [since, setSince] = useState(() => Date.now());
  const { state } = useTasks();
  const tasks = state.kind === 'ready' ? state.tasks : undefined;
  const listed = tasks?.some((task) => task.id === taskId) === true;
  const decided = answered || (listed && !cardDecisionPrompt(tasks, card));
  useEffect(() => {
    if (decided) setSince(Date.now());
  }, [decided]);
  const { data } = useQuery({
    ...taskDetailQuery(taskId),
    enabled: decided,
    refetchInterval: (current?: TaskDetail) => socialPostTaskPollMs(current, Date.now(), since),
  });
  const workspaces = useWorkspaces();
  const goesOutAt = data ? Date.parse(socialPostTaskView(data, Date.now())?.scheduledAt ?? '') : NaN;
  const [cutoffTick, setCutoffTick] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(goesOutAt)) return undefined;
    const wait = goesOutAt - Date.now();
    if (wait < 0) return undefined;
    const timer = setTimeout(() => setCutoffTick((n) => n + 1), Math.min(wait + 1, MAX_TIMER_MS));
    return () => clearTimeout(timer);
  }, [goesOutAt, cutoffTick]);
  if (!decided || !data) return null;
  const view = socialPostTaskView(data, Date.now());
  if (!view) return null;
  const slug = workspaces.status === 'ready' ? (workspaces.workspaces.find((w) => w.id === data.workspaceId)?.slug ?? '') : '';
  const tap = (action: ScheduledPostAction) => {
    logPageClickBi({ page_name: 'chat', button_name: action === 'edit' ? 'scheduled_post_edit_first' : 'scheduled_post_drop' }, { additionalFields: { task_id: taskId } });
    onTap?.(socialPostTapMessage(action, slug, taskId));
  };
  return (
    <div data-testid="socialPostTaskState" data-state={view.state}>
      <SocialOutcome
        content={view}
        locale={i18n.language || undefined}
        onViewPost={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
        onScheduledPostTap={onTap && slug && !(view.state === 'scheduled' && Date.parse(view.scheduledAt ?? '') <= Date.now()) ? tap : undefined}
        tapsHeld={!canSend}
      />
    </div>
  );
}
